import { ChatDetails, Message, Participant } from '@/components/messages';
import ChatViewModular from '@/components/messages/ChatViewModular';
import { useUserProfile } from '@/context/UserProfileContext';
import { messagingService } from '@/services/messagingService';
import {
  IonPage
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

interface ChatViewPageParams {
  chatId: string;
}

const ChatViewPage: React.FC = () => {
  const { chatId } = useParams<ChatViewPageParams>();
  const { profile } = useUserProfile();

  // State for ChatViewModular
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatDetails, setChatDetails] = useState<ChatDetails | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);

  // Fetch chat details and messages
  useEffect(() => {
    if (!chatId || !profile) return;

    const fetchChatData = async () => {
      try {
        // Fetch chat details
        const chat = await messagingService.fetchChat(chatId);
        if (chat) {
          setChatDetails({
            id: chat.id,
            name: chat.name,
            type: chat.type,
            imageUrl: chat.imageUrl,
            participants: chat.participants || [],
            createdBy: chat.createdBy
          });

          // Fetch participants
          if (chat.participants && chat.participants.length > 0) {
            const profiles = await messagingService.fetchUserProfiles(chat.participants);
            const participantsList: Participant[] = Object.values(profiles).map(user => ({
              id: user.userId,
              firstName: user.firstName,
              lastName: user.lastName,
              imageUrl: user.imageUrl,
              online: user.online,
              lastSeen: user.lastSeen
            }));
            setParticipants(participantsList);
          }
        }

        // Fetch messages
        const fetchedMessages = await messagingService.fetchMessages(chatId);
        setMessages(fetchedMessages.map(msg => ({
          id: msg.id,
          sender: msg.senderId,
          text: msg.text,
          timestamp: msg.timestamp,
          imageUrl: msg.imageUrl,
          reactions: msg.reactions,
          linkPreview: msg.linkPreview
        })));
      } catch (error) {
        console.error('Error fetching chat data:', error);
      }
    };

    fetchChatData();

    // Set up realtime subscriptions
    const messageSubscription = messagingService.subscribeToMessages(chatId, () => {
      fetchChatData(); // Refresh data when new messages arrive
    });

    return () => {
      // Clean up subscriptions
      if (messageSubscription) {
        messageSubscription.unsubscribe?.();
      }
    };
  }, [chatId, profile]);

  // Event handlers
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatId || !profile) return;

    try {
      await messagingService.sendMessage(
        chatId,
        profile.userId,
        newMessage
      );
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleImageUpload = async (file: File) => {
    // Implement image upload logic
    console.log('Image upload:', file);
  };

  const handleDeleteMessage = async (messageId: string) => {
    // Implement delete message logic
    console.log('Delete message:', messageId);
  };

  const handleAddReaction = async (messageId: string, emoji: string) => {
    // Implement add reaction logic
    console.log('Add reaction:', messageId, emoji);
  };

  const handleDeleteChat = async () => {
    // Implement delete chat logic
    console.log('Delete chat:', chatId);
  };

  return (
    <IonPage>
      {profile && (
        <ChatViewModular
          userId={profile.userId}
          activeChat={chatId}
          messages={messages}
          chatDetails={chatDetails}
          participants={participants}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onSendMessage={handleSendMessage}
          onImageUpload={handleImageUpload}
          onDeleteMessage={handleDeleteMessage}
          onAddReaction={handleAddReaction}
          onDeleteChat={handleDeleteChat}
        />
      )}
    </IonPage>
  );
};

export default ChatViewPage;
